import { StatusConfig, ArchiveReasonConfig } from "../types";

/**
 * Application-specific status configuration
 * 10-status recruitment workflow replacing legacy pipeline stages
 */

export const APPLICATION_STATUS_CONFIG: Record<string, StatusConfig> = {
  applied: { label: "Applied", color: "#8b5cf6", bgColor: "#f5f3ff" },
  matched_ai: { label: "Matched (AI)", color: "#3b82f6", bgColor: "#eff6ff" },
  not_matched_ai: {
    label: "Not Matched (AI)",
    color: "#ef4444",
    bgColor: "#fef2f2",
  },
  approved: { label: "Approved", color: "#10b981", bgColor: "#ecfdf5" },
  cv_sent: { label: "CV Sent", color: "#06b6d4", bgColor: "#ecfeff" },
  interview_waiting: { label: "Waiting", color: "#f59e0b", bgColor: "#fffbeb" },
  interview_scheduled: {
    label: "Scheduled",
    color: "#2563eb",
    bgColor: "#eff6ff",
  },
  after_interview: {
    label: "After Interview",
    color: "#7c3aed",
    bgColor: "#f5f3ff",
  },
  success: { label: "Success", color: "#14b8a6", bgColor: "#f0fdfa" },
  rejected: { label: "Rejected", color: "#6b7280", bgColor: "#f9fafb" },
};

export const APPLICATION_REJECTION_REASONS: Record<
  string,
  ArchiveReasonConfig
> = {
  client_rejected: {
    label: "Client Rejected",
    description: "Client decided not to proceed with this candidate",
  },
  candidate_withdrew: {
    label: "Candidate Withdrew",
    description: "Candidate withdrew from the process",
  },
  salary_mismatch: {
    label: "Salary Mismatch",
    description: "Could not agree on compensation",
  },
  skills_mismatch: {
    label: "Skills Mismatch",
    description: "Candidate skills did not meet requirements",
  },
  cultural_fit: {
    label: "Cultural Fit",
    description: "Not a good cultural fit for the team/company",
  },
  position_filled: {
    label: "Position Filled",
    description: "Position was filled by another candidate",
  },
  other: { label: "Other", description: "Other reason (specify in notes)" },
};

export const APPLICATION_STATUS_ORDER = [
  "applied",
  "matched_ai",
  "not_matched_ai",
  "approved",
  "cv_sent",
  "interview_waiting",
  "interview_scheduled",
  "after_interview",
  "success",
  "rejected",
];

// Kanban column order (8 columns, some statuses merged)
export const APPLICATION_KANBAN_COLUMNS = [
  { key: "applied", label: "Applied", statuses: ["applied", "not_matched_ai"] },
  { key: "matched_ai", label: "Matched", statuses: ["matched_ai"] },
  { key: "approved", label: "Approved", statuses: ["approved"] },
  { key: "cv_sent", label: "CV Sent", statuses: ["cv_sent"] },
  {
    key: "interview",
    label: "Interview",
    statuses: ["interview_waiting", "interview_scheduled"],
  },
  {
    key: "after_interview",
    label: "After Interview",
    statuses: ["after_interview"],
  },
  { key: "success", label: "Success", statuses: ["success"] },
  { key: "rejected", label: "Rejected", statuses: ["rejected"] },
];

export const APPLICATION_VALID_TRANSITIONS: Record<string, string[]> = {
  applied: ["matched_ai", "not_matched_ai", "rejected"],
  matched_ai: ["approved", "applied", "rejected"],
  not_matched_ai: ["approved", "applied", "rejected"], // approved requires override_justification
  approved: ["cv_sent", "matched_ai", "not_matched_ai", "applied", "rejected"],
  cv_sent: ["interview_waiting", "approved", "rejected"],
  interview_waiting: ["interview_scheduled", "cv_sent", "approved", "rejected"],
  interview_scheduled: ["after_interview", "interview_waiting", "rejected"],
  after_interview: [
    "success",
    "interview_scheduled",
    "interview_waiting",
    "rejected",
  ],
  success: [],
  rejected: [],
};

/**
 * Get reason why an application transition is not allowed
 */
export const getApplicationDisabledReason = (
  currentStatus: string,
  targetStatus: string,
): string | null => {
  if (currentStatus === targetStatus) {
    return "Current status";
  }

  if (currentStatus === "success") {
    return "Success is a terminal status";
  }

  if (currentStatus === "rejected") {
    return "Rejected is a terminal status";
  }

  const allowed = APPLICATION_VALID_TRANSITIONS[currentStatus] || [];
  if (!allowed.includes(targetStatus)) {
    return "Not allowed from current status";
  }

  return null;
};
